import useSWR from 'swr';
import { getConnectionPoolHistory } from '../../api';
import type { ConnectionPoolHistoryResponse } from './types/database';

interface UseConnectionPoolHistoryParams {
  hours?: number;
}

interface UseConnectionPoolHistoryReturn {
  data: ConnectionPoolHistoryResponse | undefined;
  isLoading: boolean;
  isError: any;
  mutate: () => void;
}

/**
 * Hook to fetch connection pool statistics history
 *
 * Returns historical connection pool metrics for visualizing trends over time.
 * Data is collected periodically (typically every 5 minutes) by a background job.
 *
 * @param params - Configuration options
 * @param params.hours - Number of hours of history to retrieve (1-168, default: 24)
 * @returns Connection pool history data, loading state, error state, and mutate function
 *
 * @example
 * const { data, isLoading } = useConnectionPoolHistory({ hours: 24 });
 * if (data) {
 *   console.log(`Retrieved ${data.count} data points`);
 * }
 */
export function useConnectionPoolHistory(
  params: UseConnectionPoolHistoryParams = {}
): UseConnectionPoolHistoryReturn {
  const { hours = 24 } = params;

  const { data, error, mutate } = useSWR<ConnectionPoolHistoryResponse>(
    `/admin/db-stats/connection-pool/history?hours=${hours}`,
    () => getConnectionPoolHistory(hours),
    {
      refreshInterval: 60000, // Refresh every minute
      revalidateOnMount: true,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}
